// decideChat — the validating half of the chat decider. Split out of
// `app/chatDecider.ts` (C1 hotspot decomposition; verbatim).
//
// `decideChat(state, command): Result<ChatDecision, ChatError>` — the validating
// half. A refusal (submit while already drafting, stop while idle) is an
// EXPLICIT, testable `ChatError`, never a silent no-op. On success it yields a
// `ChatDecision`: the `ChatEvent[]` that happened, the `ChatEffect[]` the
// SHELL must interpret, and the `Signal[]` other owners handle (toast, world
// re-read). Effects and Signals are DATA out of decide; it runs none of them.
// Purity: no I/O, no clock, no React. Local entry ids are a pure function of
// the session's `nextLocalId` (S4); evolve advances the counter past them.

import * as TurnLiveness from "@bilda/app/turnLiveness";
import { exhaustSwitchCase, Result } from "@bilda/utils";
import { optimisticChipId } from "../../app/composerAttachments.js";
import {
    FORM_RESUME_FALLBACK_TEXT,
    renderElicitationResumeText,
    toElicitationAnswer,
} from "../../app/elicitationAnswerMapper.js";
import type { InformationRequestEntry } from "../../components/Chat/types.js";
import type { Signal } from "../signal.js";
import type { ChatCommand } from "./command.js";
import { decideProposal } from "./decideProposal.js";
import {
    decideWaiting,
    isWaitingEvent,
    sendWhileWorking,
    waitingProgress,
} from "./decideWaiting.js";
import type { ChatDecision, ChatEffect, SendTurnEffect } from "./effect.js";
import type { ChatError } from "./error.js";
import type { ChatEvent } from "./event.js";
import {
    authorMessageHistory,
    type ConversationStanding,
    chatSessionFor,
    type FormResume,
    isAwaitingUpload,
    latestTurnActivityIds,
    resumeActivityId,
    sendableMentions,
    settleUploadChip,
    type TurnFailure,
    turnLocalIds,
    type UploadOutcome,
} from "./model.js";
import { showCreatedMaterial } from "./showCreatedMaterial.js";
import type { ChatState } from "./state.js";
import { liveStreamingMessageIds } from "./transcript.js";

export const decideChat = (
    state: ChatState,
    command: ChatCommand,
): Result.Result<ChatDecision, ChatError> => {
    switch (command.type) {
        case "submitted_turn": {
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            // PS-61: sending always works. While a turn is in flight the
            // message goes into the thread at once, waiting for the
            // assistant's next step (refused only at the CL-21 ceiling).
            if (session.drafting)
                return sendWhileWorking(
                    command.conversationId,
                    session,
                    command.text,
                    command.location,
                );
            const ids = turnLocalIds(command.conversationId, session);
            const started: ChatEvent = {
                type: "turn_started",
                conversationId: command.conversationId,
                entryId: ids.entryId,
                text: command.text,
                mentions: command.mentions,
            };
            const send: SendTurnEffect = {
                type: "send_turn",
                conversationId: command.conversationId,
                target: command.target,
                text: command.text,
                anchor: command.anchor,
                project: command.project,
                ...(command.location === null
                    ? {}
                    : { location: command.location }),
                mentions: command.mentions,
                ids: { activityId: ids.activityId },
            };
            // An attached file still uploading: the bubble shows now, the port
            // send waits until the upload lands (`source_added` releases it).
            if (isAwaitingUpload(command.mentions))
                return Result.ok({
                    events: [
                        started,
                        {
                            type: "turn_held_for_uploads",
                            conversationId: command.conversationId,
                            entryId: ids.entryId,
                            send,
                            standing: command.standing,
                        },
                    ],
                    effects: [],
                    signals: [],
                });
            const go = dispatchSend(send, command.standing);
            return Result.ok({
                events: [started, ...go.events],
                effects: go.effects,
                signals: [],
            });
        }
        case "waiting_message_accepted":
        case "waiting_action_settled":
        case "waiting_message_refused":
        case "waiting_taken_back_to_edit":
        case "requested_waiting_edit":
        case "requested_waiting_send_now":
        case "requested_waiting_delete":
        case "requested_waiting_undo":
            return Result.ok(decideWaiting(state, command));
        case "form_answer_submitted": {
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            // Same in-flight guard as `submitted_turn`: an answer submitted
            // while a turn is already running is refused. On the happy path it
            // marks drafting so the async answer→resume window (F99) refuses a
            // composer Retry / second submit until the resume turn owns the flag.
            if (session.drafting)
                return Result.err({
                    type: "submit_while_drafting",
                    conversationId: command.conversationId,
                });
            // The resume text renders the answers against the request as the
            // transcript shows it NOW, before the round-trip re-reads it.
            const request = session.conversation.find(
                (e): e is InformationRequestEntry =>
                    e.type === "information_request" &&
                    e.id === command.requestId,
            );
            return Result.ok({
                events: [
                    {
                        type: "form_answer_began",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [
                    {
                        type: "supply_information",
                        conversationId: command.conversationId,
                        answer: toElicitationAnswer(command.answers),
                        resume: {
                            requestId: command.requestId,
                            text:
                                request === undefined
                                    ? FORM_RESUME_FALLBACK_TEXT
                                    : renderElicitationResumeText(
                                          request,
                                          command.answers,
                                      ),
                            target: command.target,
                            anchor: command.anchor,
                            project: command.project,
                        },
                    },
                ],
                signals: [],
            });
        }
        case "information_supplied":
            // Guard-EXEMPT continuation of the answer→resume window (F99):
            // drafting is already on (`form_answer_began` marked it), so this
            // must NOT re-check it — otherwise the resume would refuse itself.
            return Result.ok(
                formResumeDecision(
                    state,
                    command.conversationId,
                    command.resume,
                ),
            );
        case "proposal_accept_requested":
        case "proposal_discard_requested":
        case "proposal_accepted":
        case "proposal_discarded":
        case "proposal_settle_failed":
        case "proposal_outdated":
            return Result.ok(decideProposal(command));
        case "information_supply_failed":
            return Result.ok(
                failedDecision(command.conversationId, {
                    type: "port_refused",
                    error: command.error,
                }),
            );
        case "conversation_start_failed":
            return Result.ok(
                failedDecision(command.conversationId, {
                    type: "conversation_not_started",
                }),
            );
        case "conversation_started": {
            // The conversation is durable now. Its pre-send grounding (if any)
            // is flushed before the held send goes out.
            const grounding = state.pendingGrounding.get(
                command.conversationId,
            );
            const started: Signal = {
                _tag: "Signal.ConversationStarted",
                summary: command.summary,
            };
            if (grounding !== undefined)
                return Result.ok({
                    events: [],
                    effects: [
                        {
                            type: "flush_grounding",
                            conversationId: command.conversationId,
                            grounding,
                        },
                    ],
                    signals: [started],
                });
            return Result.ok({
                ...releaseStarted(state, command.conversationId),
                signals: [started],
            });
        }
        case "pending_grounding_flushed": {
            const released = releaseStarted(state, command.conversationId);
            return Result.ok({
                events: [
                    {
                        type: "pending_grounding_was_flushed",
                        conversationId: command.conversationId,
                    },
                    ...released.events,
                ],
                effects: released.effects,
                signals: [
                    {
                        _tag: "Signal.PendingGroundingFlushed",
                        conversationId: command.conversationId,
                    },
                ],
            });
        }
        case "conversations_became_visible": {
            // Each visible conversation loads once per session; one already
            // requested (in flight or loaded) is skipped.
            const due = [...new Set(command.conversationIds)].filter(
                (id) =>
                    chatSessionFor(state.sessions, id).transcriptLoad ===
                    "unrequested",
            );
            return Result.ok({
                events: due.map(
                    (conversationId): ChatEvent => ({
                        type: "conversation_load_began",
                        conversationId,
                    }),
                ),
                effects: due.map(
                    (conversationId): ChatEffect => ({
                        type: "load_conversation",
                        conversationId,
                    }),
                ),
                signals: [],
            });
        }
        case "conversation_loaded": {
            const loaded: ChatEvent = {
                type: "conversation_was_loaded",
                conversationId: command.conversationId,
                entries: command.entries,
            };
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            // A turn running on the server that this page is not already
            // driving (#100: it reloaded mid-turn, or another page started
            // it): follow it rather than leave it looking unfinished.
            if (!command.turnRunning || session.drafting) return lifted(loaded);
            return Result.ok({
                events: [
                    loaded,
                    {
                        type: "turn_followed",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [
                    {
                        type: "follow_turn",
                        conversationId: command.conversationId,
                        ids: {
                            activityId: resumeActivityId(
                                command.conversationId,
                                session,
                            ),
                        },
                    },
                ],
                signals: [],
            });
        }
        case "followed_turn_ended": {
            // The outcome is in the log: re-read it. A followed turn the
            // Author stopped already settled (its trail id is fenced).
            const reload: ChatEffect = {
                type: "reload_transcript",
                conversationId: command.conversationId,
            };
            if (TurnLiveness.isFenced(state.liveness, command.ids.activityId))
                return effected(reload);
            return Result.ok({
                events: [
                    {
                        type: "turn_reply_landed",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [reload],
                // Re-read the world so the turn's tool side-effects surface.
                signals: [{ _tag: "Signal.WorldChanged" }],
            });
        }
        case "log_head_moved":
            return Result.ok({
                events: [],
                effects: [...state.sessions]
                    .filter(
                        ([, session]) =>
                            session.transcriptLoad === "requested" &&
                            !session.drafting,
                    )
                    .map(
                        ([conversationId]): ChatEffect => ({
                            type: "reload_transcript",
                            conversationId,
                        }),
                    ),
                signals: [],
            });
        case "conversation_load_failed":
            return lifted({
                type: "conversation_load_had_error",
                conversationId: command.conversationId,
            });
        case "retried_form_resume": {
            // GUARDED like `submitted_turn`: a Retry while a turn is in flight
            // is refused. Re-sends the same resume (F113).
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            if (session.drafting)
                return Result.err({
                    type: "submit_while_drafting",
                    conversationId: command.conversationId,
                });
            return Result.ok(
                formResumeDecision(state, command.conversationId, command),
            );
        }
        case "requested_stop": {
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            // Pre-M1: `if (!drafting) return;`. Now explicit — Stop with nothing
            // in flight is a refusal, not a no-op that quietly hit the port.
            if (!session.drafting)
                return Result.err({
                    type: "stop_while_idle",
                    conversationId: command.conversationId,
                });
            // Optimistic stop: emit `turn_stopped` so evolveChat flips drafting
            // off NOW (everything-must-feel-fast). Fence the bubbles still
            // streaming under this turn AND the turn's own activity id, so a
            // late delta/reply/failure is dropped below — even one for a turn
            // stopped before its first text streamed.
            return Result.ok({
                events: [
                    {
                        type: "turn_stopped",
                        conversationId: command.conversationId,
                        cancelledMessageIds: [
                            ...liveStreamingMessageIds(session.conversation),
                            ...latestTurnActivityIds(
                                command.conversationId,
                                session,
                            ),
                        ],
                    },
                ],
                // The server abort STILL fires — it's just no longer what the UI
                // waits on (the optimistic event already settled the UI). A
                // held send never reached the server, so there is nothing to
                // abort (`turn_stopped` drops it).
                effects:
                    session.heldSend.type === "awaiting_uploads"
                        ? []
                        : [
                              {
                                  type: "interrupt_turn",
                                  conversationId: command.conversationId,
                              },
                          ],
                signals: [],
            });
        }
        case "requested_opening_move": {
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            if (session.drafting)
                return Result.err({
                    type: "opening_move_while_drafting",
                    conversationId: command.conversationId,
                });
            return Result.ok({
                events: [
                    {
                        type: "opening_move_started",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [
                    {
                        type: "offer_opening_move",
                        conversationId: command.conversationId,
                    },
                ],
                signals: [],
            });
        }
        case "edited_draft":
            return Result.ok({
                events: [
                    {
                        type: "draft_edited",
                        conversationId: command.conversationId,
                        text: command.text,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "recalled_older_message": {
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            const history = authorMessageHistory(session.conversation);
            // Refuse when there is nothing older to reach: an empty history, or
            // already resting on the oldest entry (shell "beep at the top").
            const atOldest =
                session.historyNav.type === "at_history" &&
                session.historyNav.index >= history.length - 1;
            if (history.length === 0 || atOldest)
                return Result.err({
                    type: "no_older_message_to_recall",
                    conversationId: command.conversationId,
                });
            return Result.ok({
                events: [
                    {
                        type: "older_message_recalled",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [],
            });
        }
        case "recalled_newer_message": {
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            // Refuse when not mid-recall — there is no newer entry (or saved
            // draft) to return to from `at_draft`.
            if (session.historyNav.type === "at_draft")
                return Result.err({
                    type: "no_newer_message_to_recall",
                    conversationId: command.conversationId,
                });
            return Result.ok({
                events: [
                    {
                        type: "newer_message_recalled",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [],
            });
        }
        case "turn_progressed":
            // PS-61: the waiting messages are the queue, not the stopped
            // turn's output: after a Stop they still change (they go out as
            // the next turn), so they pass the fence.
            if (isWaitingEvent(command.event))
                return Result.ok(
                    waitingProgress(state, {
                        ...command,
                        event: command.event,
                    }),
                );
            // Late-event fence: any progress of a stopped turn (its activity id
            // fenced), or a `text_delta` whose message was cancelled by a stop,
            // must not re-grow the transcript. Drop it (an empty, effect-free
            // decision) so the stop stays final.
            if (
                TurnLiveness.isFenced(state.liveness, command.ids.activityId) ||
                (command.event.type === "text_delta" &&
                    TurnLiveness.isFenced(
                        state.liveness,
                        command.event.messageId,
                    ))
            )
                return Result.ok({ events: [], effects: [], signals: [] });
            return Result.ok({
                events: [
                    {
                        type: "turn_stream_folded",
                        conversationId: command.conversationId,
                        event: command.event,
                        ids: command.ids,
                    },
                ],
                effects: [],
                // A live material create settled → show it in the right panel.
                signals: showCreatedMaterial(state, command),
            });
        case "turn_replied": {
            // A landed proposal reaches the editor even from a stopped turn:
            // it is durable already, and the editor holds it (never lost).
            const landed: readonly Signal[] =
                command.proposal === undefined
                    ? []
                    : [{ _tag: "Signal.ProposalLanded", ...command.proposal }];
            // Late-event fence: the durable reply reuses the streamed message's
            // id, so a reply whose id was cancelled belongs to a stopped turn —
            // ignore it (don't resurrect the turn or re-flip drafting). The
            // turn's own activity id covers the rest: a stopped turn whose reply
            // never streamed (fresh id), or a reply-less one (F43b: ended on a
            // typed epilogue alone), is dropped too.
            if (
                TurnLiveness.isFenced(state.liveness, command.ids.activityId) ||
                (command.reply !== undefined &&
                    TurnLiveness.isFenced(state.liveness, command.reply.id))
            )
                return Result.ok({ events: [], effects: [], signals: landed });
            return Result.ok({
                events: [
                    {
                        type: "turn_reply_landed",
                        conversationId: command.conversationId,
                        reply: command.reply,
                    },
                ],
                // Converge the live transcript on the durable one (live ==
                // reload): the transient build card, the local-id trail and
                // any lagging tool close are superseded by the log's own
                // projection.
                effects: [
                    {
                        type: "reload_transcript",
                        conversationId: command.conversationId,
                    },
                ],
                // Re-read the world so the turn's tool side-effects surface.
                signals: [...landed, { _tag: "Signal.WorldChanged" }],
            });
        }
        case "turn_handed_over":
            // The turn goes on on another machine: follow it to its end.
            return effected({
                type: "follow_turn",
                conversationId: command.conversationId,
                ids: command.ids,
            });
        case "turn_refused": {
            // Every failure clears drafting + arms Retry (turn_failed) and raises
            // the toast; it also gets the transcript's interruption row (D18:
            // every cause, one row) and finalizes any partial stream.
            const failed = failedDecision(command.conversationId, {
                type: "port_refused",
                error: command.error,
            });
            const session = chatSessionFor(
                state.sessions,
                command.conversationId,
            );
            return Result.ok({
                ...failed,
                events: [
                    ...failed.events,
                    {
                        type: "turn_cut_short",
                        conversationId: command.conversationId,
                        entryId: command.entryId,
                        error: command.error,
                        cancelledMessageIds: liveStreamingMessageIds(
                            session.conversation,
                        ),
                    },
                ],
            });
        }
        case "opening_move_offered":
            return Result.ok({
                events: [
                    {
                        type: "opening_move_replied",
                        conversationId: command.conversationId,
                        reply: command.reply,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "opening_move_declined":
            return Result.ok({
                events: [
                    {
                        type: "opening_move_was_declined",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "opening_move_failed":
            return Result.ok({
                events: [
                    {
                        type: "opening_move_had_error",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [
                    {
                        _tag: "Signal.NoticeRaised",
                        notice: {
                            type: "turn-failed",
                            failure: {
                                type: "port_refused",
                                error: command.error,
                            },
                        },
                    },
                ],
            });
        case "conversation_transcript_loaded":
            return Result.ok({
                events: [
                    {
                        type: "conversation_transcript_was_loaded",
                        conversationId: command.conversationId,
                        entries: command.entries,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "conversation_profile_loaded":
            return Result.ok({
                events: [
                    {
                        type: "conversation_profile_was_loaded",
                        conversationId: command.conversationId,
                        profile: command.profile,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "conversation_grounded":
            return Result.ok({
                events: [
                    {
                        type: "conversation_was_grounded",
                        conversationId: command.conversationId,
                        profile: command.profile,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "conversation_ungrounded":
            return Result.ok({
                events: [
                    {
                        type: "conversation_was_ungrounded",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "conversation_selected":
            return Result.ok({
                events: [
                    {
                        type: "conversation_was_selected",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [],
            });
        case "edited_mentions":
            return lifted({
                type: "mentions_edited",
                mentions: command.mentions,
            });
        case "pinned_grounding_source":
            return lifted({
                type: "grounding_source_pinned",
                conversationId: command.conversationId,
                source: command.source,
            });
        case "unpinned_grounding_source":
            return lifted({
                type: "grounding_source_unpinned",
                conversationId: command.conversationId,
                sourceId: command.sourceId,
            });
        case "cleared_grounding_sources":
            return lifted({
                type: "grounding_sources_cleared",
                conversationId: command.conversationId,
            });
        case "source_added":
            return Result.ok(
                settleHeldUploads(state, optimisticChipId(command.ingestId), {
                    type: "upload_landed",
                    source: {
                        kind: "source",
                        id: command.source.id,
                        title: command.source.title,
                    },
                }),
            );
        case "source_upload_failed":
            return Result.ok(
                settleHeldUploads(state, optimisticChipId(command.ingestId), {
                    type: "upload_failed",
                }),
            );
        case "attached_landed_source": {
            // A held send awaiting this upload carries it already (the
            // draft that held it was spent); otherwise it joins the draft's
            // chips once (dedup by id).
            const chipId = optimisticChipId(command.ingestId);
            const claimed = [...state.sessions.values()].some(
                (s) =>
                    s.heldSend.type === "awaiting_uploads" &&
                    s.heldSend.send.mentions.some((m) => m.id === chipId),
            );
            if (
                claimed ||
                state.composerMentions.some((m) => m.id === command.source.id)
            )
                return Result.ok({ events: [], effects: [], signals: [] });
            return lifted({
                type: "mentions_edited",
                mentions: [...state.composerMentions, command.source],
            });
        }
        case "requested_source_draft":
            // Ground the fresh nascent conversation on the source (pre-send,
            // local), select it, and ask the frame to promote it to the full
            // room with the source pinned as scope.
            return Result.ok({
                events: [
                    {
                        type: "grounding_source_pinned",
                        conversationId: command.conversationId,
                        source: command.source,
                    },
                    {
                        type: "conversation_was_selected",
                        conversationId: command.conversationId,
                    },
                ],
                effects: [],
                signals: [
                    {
                        _tag: "Signal.FrameRequested",
                        transition: {
                            type: "create-material-from-source",
                            conversationId: command.conversationId,
                            source: command.source,
                        },
                    },
                ],
            });
        case "requested_resource_open":
            return Result.ok({
                events: [],
                effects: [],
                // Every reference in the chat opens in the right panel, never
                // the editor tab (owner rule, 2026-10-01); the library is the
                // panel's explicit "Open in library" choice.
                signals: [
                    {
                        _tag: "Signal.FrameRequested",
                        transition:
                            command.target === undefined
                                ? { type: "show-in-panel", ref: command.ref }
                                : {
                                      type: "show-in-panel",
                                      ref: command.ref,
                                      target: command.target,
                                  },
                    },
                ],
            });
        default:
            return exhaustSwitchCase(command);
    }
};

// A form resume (first try or Retry, F113): `turn_resumed` (no author bubble)
// + a `send_turn` carrying the request id as the persisted message's marker.
// A resume carries no @-mentions of its own.
const formResumeDecision = (
    state: ChatState,
    conversationId: string,
    resume: FormResume,
): ChatDecision => ({
    events: [
        {
            type: "turn_resumed",
            conversationId,
            requestId: resume.requestId,
            text: resume.text,
        },
    ],
    effects: [
        {
            type: "send_turn",
            conversationId,
            target: resume.target,
            text: resume.text,
            anchor: resume.anchor,
            project: resume.project,
            mentions: [],
            ids: {
                activityId: resumeActivityId(
                    conversationId,
                    chatSessionFor(state.sessions, conversationId),
                ),
            },
            resumesRequestId: resume.requestId,
        },
    ],
    signals: [],
});

// A turn failed (port refusal, failed answer, failed durable start): drafting
// clears, Retry arms, the toast shows.
const failedDecision = (
    conversationId: string,
    failure: TurnFailure,
): ChatDecision => ({
    events: [{ type: "turn_failed", conversationId, failure }],
    effects: [],
    signals: [
        {
            _tag: "Signal.NoticeRaised",
            notice: { type: "turn-failed", failure },
        },
    ],
});

// A port send ready to go: straight out on a durable conversation; on a
// nascent one it waits for the durable start (`start_conversation`), which
// `conversation_started` / `pending_grounding_flushed` release.
const dispatchSend = (
    send: SendTurnEffect,
    standing: ConversationStanding,
): Pick<ChatDecision, "events" | "effects"> =>
    standing === "durable"
        ? { events: [], effects: [send] }
        : {
              events: [
                  {
                      type: "turn_held_for_start",
                      conversationId: send.conversationId,
                      send,
                  },
              ],
              effects: [
                  {
                      type: "start_conversation",
                      conversationId: send.conversationId,
                      project: send.project,
                  },
              ],
          };

// The durable start (and grounding flush) landed: a send held for it goes out.
const releaseStarted = (
    state: ChatState,
    conversationId: string,
): Pick<ChatDecision, "events" | "effects"> => {
    const held = chatSessionFor(state.sessions, conversationId).heldSend;
    return held.type === "awaiting_start"
        ? {
              events: [{ type: "held_turn_released", conversationId }],
              effects: [held.send],
          }
        : { events: [], effects: [] };
};

// Settle one upload on every held send that awaits it; a held send left with
// no upload in flight is released — its port send goes out carrying the landed
// sources (a failed upload is left out), after the durable start when the
// conversation is still nascent.
const settleHeldUploads = (
    state: ChatState,
    chipId: string,
    outcome: UploadOutcome,
): ChatDecision => {
    const settled = [...state.sessions].flatMap(([conversationId, s]) =>
        s.heldSend.type === "awaiting_uploads" &&
        s.heldSend.send.mentions.some((m) => m.id === chipId)
            ? [
                  {
                      conversationId,
                      standing: s.heldSend.standing,
                      send: {
                          ...s.heldSend.send,
                          mentions: settleUploadChip(
                              s.heldSend.send.mentions,
                              chipId,
                              outcome,
                          ),
                      },
                  },
              ]
            : [],
    );
    const released = settled
        .filter(({ send }) => !isAwaitingUpload(send.mentions))
        .map(({ conversationId, standing, send }) => ({
            conversationId,
            go: dispatchSend(
                { ...send, mentions: sendableMentions(send.mentions) },
                standing,
            ),
        }));
    return {
        events: [
            ...settled.map(
                ({ conversationId }): ChatEvent => ({
                    type: "held_upload_settled",
                    conversationId,
                    chipId,
                    outcome,
                }),
            ),
            ...released.flatMap(({ conversationId, go }): ChatEvent[] => [
                { type: "held_turn_released", conversationId },
                ...go.events,
            ]),
        ],
        effects: released.flatMap(({ go }) => go.effects),
        signals: [],
    };
};

// A composer command that is never refused and does exactly one thing: its
// event, no effects, no Signals.
const lifted = (event: ChatEvent): Result.Result<ChatDecision, ChatError> =>
    Result.ok({ events: [event], effects: [], signals: [] });

// A command that only starts one effect: no events, no Signals.
const effected = (effect: ChatEffect): Result.Result<ChatDecision, ChatError> =>
    Result.ok({ events: [], effects: [effect], signals: [] });
